import { useCallback, useEffect, useState } from 'react';
import { api, type Product, type Stats } from '../api.js';
import {
  CATEGORY_FA,
  CONCERN_FA,
  PREGNANCY_FA,
  REVIEW_FA,
  SOURCE_FA,
  STOCK_FA,
  TRAIT_FA,
  faInt,
  formatPrice,
} from '../labels.js';

/**
 * Screen 1c.
 *
 * The catalog stopped being a table because product names here run to eighty
 * characters and a narrow column is their enemy. Reviewing is now one product
 * at a time on a sheet with room to read, and the table survives as the second
 * mode — which is where bulk actions belong, since selecting many rows is the
 * one thing a sheet cannot do.
 */

const PAGE_SIZE = 50;

/** Every trait the extractor can set, so "off" is visible as well as "on". */
const ALL_TRAITS = Object.keys(TRAIT_FA);

const REVIEW_TONE: Record<string, string> = {
  auto_approved: 'bg-brand-100 text-brand',
  approved: 'bg-ok-bg text-ok-deep',
  pending: 'bg-warn-bg text-warn-text',
  rejected: 'bg-paper-mute text-ink/50',
};

const PREGNANCY_TONE: Record<string, string> = {
  safe: 'bg-ok-bg text-ok-deep',
  unsafe: 'bg-bad-bg text-bad',
  unknown: 'bg-paper-mute text-ink/60',
};

/*
 * The shop's own rating, 1 to 5, centred on 3.
 *
 * Read-only here on purpose. It is entered in WooCommerce, next to the
 * product, because that is where the team already works; leaving it editable
 * in two places would give one field two owners, and the next sync would
 * quietly erase whatever was set in the panel.
 */
function RankStrip({ value }: { value: number | null }) {
  return (
    <div className="flex items-center gap-2.5">
      <span className="text-xs text-ink/45">رتبه‌ی فروشگاه</span>
      <div className="flex gap-1">
        {[1, 2, 3, 4, 5].map((n) => (
          <span
            key={n}
            className={`flex h-[26px] w-[26px] items-center justify-center rounded text-xs ${
              value === n
                ? 'bg-ink-strong font-bold text-white'
                : 'bg-paper-mute font-medium text-ink/35'
            }`}
          >
            {faInt(n)}
          </span>
        ))}
      </div>
    </div>
  );
}

function Tile({
  label,
  value,
  tone,
  highlight,
}: {
  label: string;
  value: number;
  tone?: string | undefined;
  highlight?: boolean | undefined;
}) {
  return (
    /* Card in a grid on a phone; one band divided by hairlines on desktop. */
    <div
      className={`rounded-xl border border-ink/[.09] p-3.5 md:rounded-none md:border-0 md:border-e md:border-ink/[.08] md:px-6 md:py-4 md:last:border-e-0 ${
        highlight ? 'bg-warn-panel' : ''
      }`}
    >
      <div className={`text-xs ${highlight ? 'font-medium text-warn-deep' : 'text-ink/50'}`}>
        {label}
      </div>
      <div className={`mt-1.5 text-md font-bold md:mt-2 md:text-xl ${tone ?? ''}`}>
        {faInt(value)}
      </div>
    </div>
  );
}

function Thumb({ product, size }: { product: Product; size: string }) {
  return product.imageUrl ? (
    <img
      src={product.imageUrl}
      alt=""
      loading="lazy"
      className={`${size} shrink-0 rounded-md bg-paper-mute object-cover`}
    />
  ) : (
    <div className={`${size} shrink-0 rounded-md bg-paper-mute`} />
  );
}

export function Catalog({ inboxId }: { inboxId?: string | undefined }) {
  const [stats, setStats] = useState<Stats | null>(null);
  const [rows, setRows] = useState<Product[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [cursor, setCursor] = useState(0);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(true);
  const [mode, setMode] = useState<'sheet' | 'table'>('sheet');
  const [syncing, setSyncing] = useState(false);
  const [lastSyncedAt, setLastSyncedAt] = useState<string | null>(null);
  /** Reviewed in this sitting — the progress bar is about the session's work. */
  const [done, setDone] = useState(0);
  const [filters, setFilters] = useState({
    q: '',
    category: '',
    pregnancy: '',
    review: 'pending',
    /** '' = live stock (the server's default) · 'dropped' = what was removed. */
    published: '',
  });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params: Record<string, string> = { page: String(page) };
      if (inboxId) params['inboxId'] = inboxId;
      for (const [k, v] of Object.entries(filters)) if (v) params[k] = v;
      const [s, p] = await Promise.all([api.stats(inboxId), api.products(params)]);
      setStats(s);
      setRows(p.products);
      setTotal(p.total);
      setSelected(new Set());
      setCursor((c) => Math.min(c, Math.max(0, p.products.length - 1)));
    } finally {
      setLoading(false);
    }
  }, [page, filters, inboxId]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    if (!inboxId) return;
    void api
      .inboxes()
      .then(({ inboxes }) =>
        setLastSyncedAt(inboxes.find((i) => i.id === inboxId)?.lastSyncedAt ?? null),
      )
      .catch(() => setLastSyncedAt(null));
  }, [inboxId]);

  const current = rows[cursor] ?? null;

  const advance = useCallback(() => {
    setCursor((c) => Math.min(c + 1, Math.max(0, rows.length - 1)));
  }, [rows.length]);

  /**
   * Judge one, move on.
   *
   * The row stays on screen after the verdict rather than vanishing: the list
   * is refetched only when the reviewer asks for it, so the ground does not
   * move under someone working down a queue.
   */
  const judge = useCallback(
    async (status: 'approved' | 'rejected') => {
      if (!current) return;
      await api.review([current.id], { reviewStatus: status });
      setRows((rs) =>
        rs.map((r) => (r.id === current.id ? { ...r, reviewStatus: status } : r)),
      );
      setDone((d) => d + 1);
      advance();
    },
    [current, advance],
  );

  useEffect(() => {
    if (mode !== 'sheet') return;
    function onKey(e: KeyboardEvent) {
      const el = e.target as HTMLElement | null;
      // Never steal a keystroke from someone typing in the search box.
      if (el && ['INPUT', 'TEXTAREA', 'SELECT'].includes(el.tagName)) return;
      if (e.key === 'Enter') void judge('approved');
      else if (e.key === 'r' || e.key === 'R') void judge('rejected');
      // The panel is RTL, so forward is left. The mockup drew a right arrow,
      // which points backwards in this direction of reading.
      else if (e.key === 'ArrowLeft') advance();
      else if (e.key === 'ArrowRight') setCursor((c) => Math.max(0, c - 1));
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [mode, judge, advance]);

  async function setCategory(value: string) {
    if (!current) return;
    await api.review([current.id], { functionalCategory: value || null });
    setRows((rs) =>
      rs.map((r) =>
        r.id === current.id ? { ...r, category: value || null, source: 'manual' } : r,
      ),
    );
  }

  async function bulk(patch: Record<string, unknown>) {
    if (selected.size === 0) return;
    await api.review([...selected], patch);
    await load();
  }

  async function syncNow() {
    if (!inboxId) return;
    setSyncing(true);
    try {
      const { syncedAt } = await api.syncCatalog(inboxId);
      setLastSyncedAt(syncedAt);
      await load();
    } finally {
      setSyncing(false);
    }
  }

  const unsafe = stats?.byPregnancy.find((p) => p.status === 'unsafe')?.n ?? 0;
  const categorised = (stats?.totalProducts ?? 0) - (stats?.uncategorized ?? 0);
  const pending = stats?.byReview.find((r) => r.status === 'pending')?.n ?? 0;
  const pages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const staleDays = lastSyncedAt
    ? Math.floor((Date.now() - new Date(lastSyncedAt).getTime()) / 86_400_000)
    : null;

  return (
    <div className="flex min-h-0 flex-1 flex-col bg-white">
      <header className="flex flex-col gap-3 border-b border-ink/[.08] px-4 pb-4 pt-4 md:flex-row md:items-end md:justify-between md:gap-4 md:px-7 md:pt-6">
        <div>
          <h1 className="text-xl font-bold tracking-tight md:text-2xl">کاتالوگ</h1>
          <div className="mt-1.5 text-sm text-ink/50 md:mt-2 md:text-base">
            {faInt(stats?.totalProducts ?? 0)} محصول · {faInt(stats?.brands ?? 0)} برند ·{' '}
            <span
              className={
                staleDays === null || staleDays >= 2 ? 'font-medium text-warn-text' : ''
              }
            >
              {staleDays === null
                ? 'هنوز سینک نشده'
                : staleDays === 0
                  ? 'امروز سینک شد'
                  : `آخرین سینک ${faInt(staleDays)} روز پیش`}
            </span>
          </div>
        </div>

        <div className="flex items-center gap-2.5">
          <div className="flex rounded-xl bg-paper-mute p-[3px]">
            {(
              [
                ['sheet', 'بازبینی پشت‌سرهم'],
                ['table', 'جدول'],
              ] as const
            ).map(([value, label]) => (
              <button
                key={value}
                onClick={() => setMode(value)}
                className={`rounded-md px-4 py-2 text-sm transition ${
                  mode === value
                    ? 'bg-white font-bold shadow-raise'
                    : 'font-medium text-ink/55'
                }`}
              >
                {label}
              </button>
            ))}
          </div>
          <button
            className="btn-ghost px-3.5 py-2.5 text-sm"
            disabled={syncing || !inboxId}
            onClick={() => void syncNow()}
          >
            {syncing ? 'در حال سینک…' : 'سینک کاتالوگ'}
          </button>
        </div>
      </header>

      <div className="grid grid-cols-2 gap-2.5 p-4 md:grid-cols-5 md:gap-0 md:border-b md:border-ink/[.08] md:p-0">
        <Tile label="کل محصولات" value={stats?.totalProducts ?? 0} />
        <Tile label="دسته‌بندی‌شده" value={categorised} tone="text-ok-deep" />
        <Tile label="بدون دسته" value={stats?.uncategorized ?? 0} />
        <Tile label="در انتظار بازبینی" value={pending} tone="text-warn-deep" highlight />
        {/* One tile, not three. Pregnancy is a filter that applies to the few
            customers who raise it, not the shape of the whole catalog. */}
        <Tile label="ناایمن در بارداری" value={unsafe} tone="text-bad" />
      </div>

      {mode === 'sheet' ? (
        <div className="flex min-h-0 flex-1 flex-col overflow-y-auto md:flex-row md:overflow-visible">
          <div className="flex min-w-0 flex-1 flex-col bg-paper-app px-4 py-4 md:px-7 md:py-6">
            <div className="flex items-center justify-between gap-4">
              <div className="text-sm text-ink/50">
                <span className="font-bold text-ink">{faInt(done)}</span> بازبینی شد ·{' '}
                {faInt(Math.max(0, total - done))} باقی مانده
              </div>
              <div className="hidden items-center gap-2 text-xs text-ink/45 md:flex">
                <kbd className="rounded-sm bg-ok-bg px-2 py-1 font-mono text-2xs font-bold text-ok-deep">
                  ↵
                </kbd>
                تأیید
                <kbd className="rounded-sm bg-bad-bg px-2 py-1 font-mono text-2xs font-bold text-bad">
                  R
                </kbd>
                رد
                <kbd className="rounded-sm bg-paper-mute px-2 py-1 font-mono text-2xs font-bold">
                  ←
                </kbd>
                بعدی
              </div>
            </div>
            <div className="mt-2.5 h-[5px] rounded-sm bg-ink/[.08]">
              <div
                className="h-[5px] rounded-sm bg-ok transition-all"
                style={{ width: `${total > 0 ? Math.min(100, (done / total) * 100) : 0}%` }}
              />
            </div>

            {!current && (
              <div className="card mt-5 flex flex-1 items-center justify-center">
                <p className="text-sm text-ink/40">
                  {loading ? 'در حال بارگذاری…' : 'در این فیلتر محصولی نمانده.'}
                </p>
              </div>
            )}

            {current && (
              <div className="card mt-4 flex flex-1 flex-col p-4 md:mt-5 md:p-6">
                <div className="flex gap-4 md:gap-5">
                  <Thumb
                    product={current}
                    size="h-[84px] w-[84px] rounded-xl md:h-[124px] md:w-[124px]"
                  />
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2 text-xs text-ink/50">
                      {current.brand && (
                        <>
                          <span className="font-medium text-ink">{current.brand}</span>
                          <span className="text-ink/20">·</span>
                        </>
                      )}
                      <span className={current.stockStatus === 'instock' ? '' : 'text-bad'}>
                        {STOCK_FA[current.stockStatus]}
                        {current.stockQuantity != null && ` ${faInt(current.stockQuantity)}`}
                      </span>
                      <span className="text-ink/20">·</span>
                      <span className="font-bold text-ink">{formatPrice(current.price)}</span>
                      <span className={`badge ${REVIEW_TONE[current.reviewStatus ?? 'pending']}`}>
                        {REVIEW_FA[current.reviewStatus ?? 'pending']}
                      </span>
                    </div>

                    <h2 className="mt-2 line-clamp-2 text-md font-bold tracking-tight md:mt-2.5 md:text-xl">
                      {current.permalink ? (
                        <a href={current.permalink} target="_blank" rel="noreferrer">
                          {current.name}
                        </a>
                      ) : (
                        current.name
                      )}
                    </h2>

                    <div className="mt-2.5 font-mono text-xs text-ink/40" dir="ltr">
                      {current.sku ? `SKU ${current.sku} · ` : ''}woo/{current.wooId} · synced{' '}
                      {new Date(current.syncedAt).toLocaleDateString('fa-IR', {
                        day: 'numeric',
                        month: 'long',
                      })}
                    </div>
                  </div>
                </div>

                <div className="mt-5 grid grid-cols-1 gap-5 md:mt-6 md:grid-cols-2 md:gap-6">
                  <div>
                    <div className="field-label">دسته</div>
                    <select
                      className="input mt-2 font-medium"
                      value={current.category ?? ''}
                      onChange={(e) => void setCategory(e.target.value)}
                    >
                      <option value="">— بدون دسته —</option>
                      {Object.entries(CATEGORY_FA).map(([k, v]) => (
                        <option key={k} value={k}>
                          {v}
                        </option>
                      ))}
                    </select>
                    <div className="mt-2 text-xs leading-relaxed text-ink/45">
                      {current.source
                        ? `پیشنهاد از ${SOURCE_FA[current.source] ?? current.source}`
                        : 'منبعی ثبت نشده'}
                      {current.reviewStatus === 'pending' && (
                        <span className="font-medium text-warn-text"> · تأیید نشده</span>
                      )}
                    </div>
                  </div>

                  <div>
                    <div className="field-label">وضعیت بارداری</div>
                    <div className="mt-2 flex rounded-xl bg-paper-mute p-[3px]">
                      {(['safe', 'unknown', 'unsafe'] as const).map((s) => (
                        <span
                          key={s}
                          className={`flex-1 rounded-md py-2 text-center text-sm ${
                            current.pregnancyStatus === s
                              ? `${PREGNANCY_TONE[s]} font-bold`
                              : 'font-medium text-ink/55'
                          }`}
                        >
                          {PREGNANCY_FA[s]}
                        </span>
                      ))}
                    </div>
                    {/* Not a control. It is computed from the confirmed
                        ingredients (D-020), so the way to change it is to fix
                        the ingredients — and "unknown" is treated exactly like
                        "unsafe" by the recommendation filter. */}
                    <div className="mt-2 text-xs leading-relaxed text-ink/45">
                      از ترکیبات تأییدشده حساب می‌شود · دستی تغییر نمی‌کند
                    </div>
                  </div>
                </div>

                <div className="mt-6">
                  <div className="flex items-baseline justify-between">
                    <div className="field-label">مواد مؤثر</div>
                    <div className="text-xs text-ink/35">
                      {faInt(current.ingredients.length)} پیوند
                    </div>
                  </div>
                  <div className="mt-2.5 flex flex-wrap gap-2">
                    {current.ingredients.map((n) => (
                      <span
                        key={n}
                        className="rounded bg-brand-100 px-3 py-1.5 text-xs font-medium text-brand"
                      >
                        {n}
                      </span>
                    ))}
                    {current.concerns?.map((c) => (
                      <span key={c} className="rounded bg-paper-mute px-3 py-1.5 text-xs text-ink/65">
                        {CONCERN_FA[c] ?? c}
                      </span>
                    ))}
                    {current.ingredients.length === 0 && (
                      <span className="text-sm text-ink/40">
                        {current.ingredientsRaw
                          ? 'لیست ترکیبات هست ولی با واژگان ما نخورد'
                          : 'ترکیباتی از ووکامرس نیامده'}
                      </span>
                    )}
                  </div>
                  {current.ingredientsRaw && (
                    <p className="mt-2.5 line-clamp-2 text-xs leading-relaxed text-ink/40">
                      {current.ingredientsRaw}
                    </p>
                  )}
                </div>

                <div className="mt-6">
                  {/* The data was already there and had nowhere to be seen. */}
                  <div className="field-label">ویژگی‌ها — از متن خود محصول</div>
                  <div className="mt-2.5 flex flex-wrap gap-2">
                    {ALL_TRAITS.map((t) => {
                      const on = current.traits?.includes(t);
                      return (
                        <span
                          key={t}
                          className={`rounded px-3 py-1.5 text-xs ${
                            on
                              ? 'bg-ink font-medium text-white'
                              : 'border border-ink/[.14] text-ink/50'
                          }`}
                        >
                          {TRAIT_FA[t]}
                        </span>
                      );
                    })}
                  </div>
                </div>

                <div className="mt-auto flex flex-wrap items-center gap-2.5 pt-5 md:pt-6">
                  <button className="btn-primary px-6" onClick={() => void judge('approved')}>
                    تأیید و بعدی
                  </button>
                  <button className="btn-danger" onClick={() => void judge('rejected')}>
                    رد
                  </button>
                  <button className="btn-quiet" onClick={advance}>
                    رد کردن این یکی
                  </button>
                  <div className="w-full md:ms-auto md:w-auto">
                    <RankStrip value={current.editorialRank} />
                  </div>
                </div>
              </div>
            )}
          </div>

          <div className="flex shrink-0 flex-col border-t border-ink/[.08] md:w-[306px] md:border-s md:border-t-0">
            <div className="border-b border-ink/[.08] p-4">
              <input
                className="input py-2.5"
                placeholder={`جستجو در ${faInt(total)} مورد`}
                value={filters.q}
                onChange={(e) => {
                  setPage(1);
                  setFilters({ ...filters, q: e.target.value });
                }}
              />
              <div className="mt-2.5 flex flex-wrap gap-1.5">
                {(
                  [
                    [{ review: 'pending', category: '', pregnancy: '', published: '' }, 'در انتظار'],
                    [{ review: '', category: 'none', pregnancy: '', published: '' }, 'بدون دسته'],
                    [{ review: '', category: '', pregnancy: 'unsafe', published: '' }, 'بارداری'],
                    [{ review: '', category: '', pregnancy: '', published: '' }, 'همه'],
                    [{ review: '', category: '', pregnancy: '', published: 'dropped' }, 'کنارگذاشته'],
                  ] as const
                ).map(([patch, label]) => {
                  const on =
                    filters.review === patch.review &&
                    filters.category === patch.category &&
                    filters.pregnancy === patch.pregnancy &&
                    filters.published === patch.published;
                  return (
                    <button
                      key={label}
                      onClick={() => {
                        setPage(1);
                        setCursor(0);
                        setFilters({ ...filters, ...patch });
                      }}
                      className={on ? 'chip-on' : 'chip'}
                    >
                      {label}
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="max-h-[320px] overflow-y-auto md:max-h-none md:flex-1">
              {rows.map((p, i) => (
                <button
                  key={p.id}
                  onClick={() => setCursor(i)}
                  className={`flex w-full gap-2.5 border-b border-ink/[.07] p-3 text-start transition ${
                    i === cursor
                      ? 'border-e-[3px] border-e-brand bg-brand-row'
                      : 'hover:bg-paper-app'
                  }`}
                >
                  <Thumb product={p} size="h-[38px] w-[38px]" />
                  <div className="min-w-0">
                    <div
                      className={`line-clamp-2 text-sm leading-normal ${
                        i === cursor ? 'font-medium' : 'text-ink/70'
                      }`}
                    >
                      {p.name}
                    </div>
                    <div
                      className={`mt-1 text-2xs font-medium ${
                        i === cursor
                          ? 'text-brand'
                          : p.pregnancyStatus === 'unsafe'
                            ? 'text-bad'
                            : !p.category
                              ? 'text-warn-text'
                              : 'text-ink/40'
                      }`}
                    >
                      {i === cursor
                        ? 'در حال بازبینی'
                        : p.pregnancyStatus === 'unsafe'
                          ? 'ناایمن در بارداری'
                          : !p.category
                            ? 'بدون دسته'
                            : REVIEW_FA[p.reviewStatus ?? 'pending']}
                    </div>
                  </div>
                </button>
              ))}
              {rows.length === 0 && !loading && (
                <p className="p-6 text-center text-sm text-ink/40">چیزی پیدا نشد</p>
              )}
              {pages > 1 && (
                <div className="flex items-center justify-between gap-2 p-3 text-xs">
                  <button className="chip" disabled={page <= 1} onClick={() => setPage(page - 1)}>
                    قبلی
                  </button>
                  <span className="text-ink/40">
                    {faInt(page)} از {faInt(pages)}
                  </span>
                  <button className="chip" disabled={page >= pages} onClick={() => setPage(page + 1)}>
                    بعدی
                  </button>
                </div>
              )}
            </div>

            <div className="border-t border-ink/[.08] bg-paper p-4 text-xs leading-relaxed text-ink/50">
              عملیات گروهی در نمای <span className="font-medium text-ink">جدول</span> است:
              انتخاب چندتایی، تأیید یا رد یک‌جا.
            </div>
          </div>
        </div>
      ) : (
        <div className="flex-1 overflow-auto p-6">
          <div className="mb-4 grid gap-3 md:grid-cols-3 lg:grid-cols-5">
            <input
              className="input"
              placeholder="جستجو در نام محصول…"
              value={filters.q}
              onChange={(e) => {
                setPage(1);
                setFilters({ ...filters, q: e.target.value });
              }}
            />
            <select
              className="input"
              value={filters.category}
              onChange={(e) => {
                setPage(1);
                setFilters({ ...filters, category: e.target.value });
              }}
            >
              <option value="">همه دسته‌ها</option>
              {Object.entries(CATEGORY_FA).map(([k, v]) => (
                <option key={k} value={k}>
                  {v}
                </option>
              ))}
              <option value="none">بدون دسته</option>
            </select>
            <select
              className="input"
              value={filters.pregnancy}
              onChange={(e) => {
                setPage(1);
                setFilters({ ...filters, pregnancy: e.target.value });
              }}
            >
              <option value="">هر وضعیت بارداری</option>
              {Object.entries(PREGNANCY_FA).map(([k, v]) => (
                <option key={k} value={k}>
                  {v}
                </option>
              ))}
            </select>
            <select
              className="input"
              value={filters.published}
              onChange={(e) => {
                setPage(1);
                setFilters({ ...filters, published: e.target.value });
              }}
            >
              {/* Dropped rows are kept but are not the catalog: hidden in
                  WooCommerce, private, from an excluded brand, or gone from the
                  shop entirely. None of them can be recommended. */}
              <option value="">فقط موجود در فروشگاه</option>
              <option value="dropped">کنارگذاشته‌ها</option>
              <option value="any">همه، از جمله کنارگذاشته‌ها</option>
            </select>
            <select
              className="input"
              value={filters.review}
              onChange={(e) => {
                setPage(1);
                setFilters({ ...filters, review: e.target.value });
              }}
            >
              <option value="">هر وضعیت بازبینی</option>
              {Object.entries(REVIEW_FA).map(([k, v]) => (
                <option key={k} value={k}>
                  {v}
                </option>
              ))}
            </select>
          </div>

          {selected.size > 0 && (
            <div className="mb-4 flex flex-wrap items-center gap-2 rounded-xl bg-brand-row p-3">
              <span className="text-sm font-medium text-brand">
                {faInt(selected.size)} محصول انتخاب شده
              </span>
              <button
                className="btn-primary px-4 py-2 text-sm"
                onClick={() => void bulk({ reviewStatus: 'approved' })}
              >
                تأیید گروهی
              </button>
              <button
                className="btn-danger px-4 py-2 text-sm"
                onClick={() => void bulk({ reviewStatus: 'rejected' })}
              >
                رد گروهی
              </button>
              <button
                className="btn-quiet px-3 py-2 text-sm"
                onClick={() => setSelected(new Set())}
              >
                لغو انتخاب
              </button>
            </div>
          )}

          <div className="card overflow-hidden">
            <div className="overflow-x-auto">
            <table className="w-full min-w-[820px] text-sm">
              <thead className="bg-paper text-start text-xs text-ink/50">
                <tr>
                  <th className="w-10 p-3">
                    <input
                      type="checkbox"
                      checked={rows.length > 0 && selected.size === rows.length}
                      onChange={(e) =>
                        setSelected(e.target.checked ? new Set(rows.map((r) => r.id)) : new Set())
                      }
                    />
                  </th>
                  <th className="p-3 text-start font-medium">محصول</th>
                  <th className="p-3 text-start font-medium">دسته</th>
                  <th className="p-3 text-start font-medium">مواد مؤثر</th>
                  <th className="p-3 text-start font-medium">موجودی</th>
                  <th className="p-3 text-start font-medium">قیمت</th>
                  <th
                    className="p-3 text-start font-medium"
                    title="از ووکامرس می‌آید (فیلد quality_rank). ۳ خنثی است؛ کمتر پایین می‌آورد، بیشتر بالا."
                  >
                    رتبه
                  </th>
                  <th className="p-3 text-start font-medium">بازبینی</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-ink/[.07]">
                {loading && (
                  <tr>
                    <td colSpan={8} className="p-8 text-center text-ink/40">
                      در حال بارگذاری…
                    </td>
                  </tr>
                )}
                {!loading && rows.length === 0 && (
                  <tr>
                    <td colSpan={8} className="p-8 text-center text-ink/40">
                      محصولی پیدا نشد
                    </td>
                  </tr>
                )}
                {!loading &&
                  rows.map((p) => (
                    <tr
                      key={p.id}
                      className={selected.has(p.id) ? 'bg-brand-row' : 'hover:bg-paper-app'}
                    >
                      <td className="p-3">
                        <input
                          type="checkbox"
                          checked={selected.has(p.id)}
                          onChange={(e) => {
                            const next = new Set(selected);
                            if (e.target.checked) next.add(p.id);
                            else next.delete(p.id);
                            setSelected(next);
                          }}
                        />
                      </td>
                      <td className="max-w-md p-3">
                        <div className="flex items-center gap-2">
                          <Thumb product={p} size="h-9 w-9" />
                          <a
                            href={p.permalink ?? '#'}
                            target="_blank"
                            rel="noreferrer"
                            className="line-clamp-2 hover:text-brand"
                          >
                            {p.name}
                          </a>
                        </div>
                      </td>
                      <td className="whitespace-nowrap p-3">
                        {p.category ? CATEGORY_FA[p.category] : <span className="text-ink/25">—</span>}
                        {p.spf && <span className="ms-1 text-xs text-ink/40">SPF {faInt(p.spf)}</span>}
                      </td>
                      <td className="p-3">
                        <div className="flex flex-wrap gap-1">
                          {p.ingredients.length === 0 && <span className="text-ink/25">—</span>}
                          {p.ingredients.map((n) => (
                            <span key={n} className="badge bg-paper-mute text-ink/60">
                              {n}
                            </span>
                          ))}
                        </div>
                      </td>
                      <td className="whitespace-nowrap p-3">
                        <span className={p.stockStatus === 'instock' ? 'text-ok' : 'text-ink/40'}>
                          {STOCK_FA[p.stockStatus]}
                        </span>
                      </td>
                      <td className="whitespace-nowrap p-3 text-ink/60">{formatPrice(p.price)}</td>
                      <td className="whitespace-nowrap p-3">
                        {p.editorialRank ? (
                          <span
                            className={`badge ${
                              p.editorialRank >= 4
                                ? 'bg-ok-bg text-ok-deep'
                                : p.editorialRank <= 2
                                  ? 'bg-bad-bg text-bad'
                                  : 'bg-paper-mute text-ink/60'
                            }`}
                          >
                            {faInt(p.editorialRank)}
                          </span>
                        ) : (
                          <span className="text-ink/25">—</span>
                        )}
                      </td>
                      <td className="whitespace-nowrap p-3">
                        <span className={`badge ${REVIEW_TONE[p.reviewStatus ?? 'pending']}`}>
                          {REVIEW_FA[p.reviewStatus ?? 'pending']}
                        </span>
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
            </div>

            <div className="flex items-center justify-between border-t border-ink/[.07] p-3 text-sm">
              <span className="text-ink/50">
                {faInt(total)} محصول · صفحه {faInt(page)} از {faInt(pages)}
              </span>
              <div className="flex gap-2">
                <button
                  className="btn-ghost px-3 py-2"
                  disabled={page <= 1}
                  onClick={() => setPage(page - 1)}
                >
                  قبلی
                </button>
                <button
                  className="btn-ghost px-3 py-2"
                  disabled={page >= pages}
                  onClick={() => setPage(page + 1)}
                >
                  بعدی
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
